import React, { useState, useEffect } from 'react';
import {
  saveCountdownImage,
  deleteCountdownImage,
  clearAllCountdownImages
} from '../../services/countdownImageService';
import {
  getAdminDebugModeAppLevel,
  saveAdminDebugModeAppLevel
} from '../../services/appLevelSettings';
import { Settings, Upload, Image, Trash2, Users, Bug } from 'lucide-react';

const AdminPreferences = ({ 
  defaultTab, 
  onDefaultTabChange,
  countdownImages = [],
  onCountdownImagesChange
}) => {
  const [selectedTab, setSelectedTab] = useState(defaultTab || 'viewer');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [, setImagePreview] = useState(null);
  const [debugMode, setDebugMode] = useState(false);

  // Load debug mode and sync between localStorage and Supabase
  useEffect(() => {
    const loadDebugMode = async () => {
      try {
        // Get current values from both sources
        const localDebugMode = localStorage.getItem('admin_debug_mode') === 'true';
        const supabaseDebugMode = await getAdminDebugModeAppLevel();
        
        // If they match, use the value
        if (localDebugMode === supabaseDebugMode) {
          setDebugMode(localDebugMode);
          console.log('🔄 Debug mode already synced:', localDebugMode);
          return;
        }
        
        // If they don't match, prioritize localStorage (more recent user action)
        // and update Supabase to match
        console.log('🔄 Debug mode mismatch - localStorage:', localDebugMode, 'Supabase:', supabaseDebugMode);
        console.log('🔄 Prioritizing localStorage value and syncing to Supabase');
        
        setDebugMode(localDebugMode);
        await saveAdminDebugModeAppLevel(localDebugMode);
        console.log('✅ Debug mode synced localStorage → Supabase:', localDebugMode);
        
      } catch (error) {
        console.error('❌ Error loading debug mode:', error);
        // Fallback to localStorage if Supabase fails
        const localDebugMode = localStorage.getItem('admin_debug_mode') === 'true';
        setDebugMode(localDebugMode);
        console.log('🔄 Fallback to localStorage debug mode:', localDebugMode);
      }
    };
    
    loadDebugMode();
  }, []);

  // Available tabs for non-admin users
  const availableTabs = [
    { id: 'viewer', name: 'Viewer', icon: '👁️', description: 'Movie schedules and reviews' },
    { id: 'survey', name: 'Survey', icon: '🗳️', description: 'Movie voting and surveys' },
    { id: 'reviews', name: 'Reviews', icon: '⭐', description: 'Movie reviews and ratings' }
  ];

  const handleTabChange = (tabId) => {
    setSelectedTab(tabId);
    onDefaultTabChange(tabId);
    // Note: onDefaultTabChange now handles app-level persistence
  };

  const handleDebugModeChange = async (enabled) => {
    setDebugMode(enabled);
    
    // Update localStorage immediately for UI responsiveness
    localStorage.setItem('admin_debug_mode', enabled.toString());
    
    // Also persist to app-level storage for cross-session persistence
    try {
      await saveAdminDebugModeAppLevel(enabled);
      console.log('✅ Debug mode saved to both localStorage and Supabase:', enabled);
    } catch (error) {
      console.error('❌ Error saving debug mode to Supabase:', error);
      // Keep localStorage updated even if Supabase fails
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB.');
      return;
    }

    setUploadingImage(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageData = {
          id: Date.now(), // Temporary ID, will be replaced by Supabase UUID
          data: e.target.result,
          name: file.name,
          size: file.size,
          mimeType: file.type,
          uploadedAt: new Date().toISOString()
        };

        try {
          // Save to Supabase via queue (automatically handles fallbacks)
          const result = await saveCountdownImage(imageData);
          
          if (result.error) {
            throw result.error;
          }

          // Update local state with the saved image
          const updatedImages = [...countdownImages, result.data];
          onCountdownImagesChange(updatedImages);
          
          setImagePreview(null);
          setUploadingImage(false);
          
          alert('✅ Image uploaded successfully!');
          
        } catch (saveError) {
          console.error('Error saving image:', saveError);
          alert('❌ Failed to save image. Please try again.\n\nError: ' + (saveError.message || 'Unknown error'));
          setUploadingImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
      setUploadingImage(false);
    }

    // Reset file input
    event.target.value = '';
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this countdown image?')) return;

    try {
      // Delete from Supabase
      const { error } = await deleteCountdownImage(imageId);
      
      if (error) {
        throw error;
      }

      // Update local state
      const updatedImages = countdownImages.filter(img => img.id !== imageId);
      onCountdownImagesChange(updatedImages);
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  const handleClearAllImages = async () => {
    if (!window.confirm('Delete all countdown images? This cannot be undone.')) return;

    try {
      // Clear from Supabase
      const { error } = await clearAllCountdownImages();
      
      if (error) {
        throw error;
      }

      // Update local state
      onCountdownImagesChange([]);
    } catch (error) {
      console.error('Error clearing images:', error);
      alert('Failed to clear images. Please try again.');
    }
  };

  return (
    <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Settings className="text-orange-400" size={24} />
        <h2 className="text-xl font-bold text-orange-300">Admin Preferences</h2>
      </div>

      <div className="space-y-6">
        {/* Debug Mode Setting */}
        <div>
          <h3 className="text-orange-300 font-semibold mb-3 flex items-center gap-2">
            <Bug size={18} />
            Debug Mode
          </h3>
          <p className="text-orange-200 text-sm mb-4">
            Enable debug mode to show additional admin controls and debugging information.
          </p>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleDebugModeChange(!debugMode)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all border-2 ${
                debugMode
                  ? 'border-orange-500 bg-orange-900 bg-opacity-30 text-orange-300'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-orange-600'
              }`}
            >
              <div className="flex items-center gap-2">
                <Bug size={16} />
                <span>Debug Mode: {debugMode ? 'ON' : 'OFF'}</span>
              </div>
            </button>
            {debugMode && (
              <span className="text-orange-400 text-sm">⚠️ Admin toggle visible in navigation</span>
            )}
          </div>
        </div>

        {/* Default Tab Setting */}
        <div>
          <h3 className="text-orange-300 font-semibold mb-3 flex items-center gap-2">
            <Users size={18} />
            Default Tab for Non-Admin Users
          </h3>
          <p className="text-orange-200 text-sm mb-4">
            Choose which tab non-admin users see when they first visit the site.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {availableTabs.map((tab, index) => (
              <button
                key={tab.id || `tab-${index}`}
                onClick={() => handleTabChange(tab.id)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  selectedTab === tab.id
                    ? 'border-orange-500 bg-orange-900 bg-opacity-30'
                    : 'border-gray-600 bg-gray-700 hover:border-orange-600'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{tab.icon}</span>
                  <span className="font-semibold text-orange-300">{tab.name}</span>
                  {selectedTab === tab.id && (
                    <div className="ml-auto w-3 h-3 bg-orange-500 rounded-full"></div>
                  )}
                </div>
                <p className="text-gray-300 text-xs">{tab.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Countdown Images */}
        <div>
          <h3 className="text-orange-300 font-semibold mb-3 flex items-center gap-2">
            <Image size={18} />
            Countdown End Images
          </h3>
          <p className="text-orange-200 text-sm mb-4">
            Upload images to display randomly when the pre-movie countdown ends. These will appear during the "GOOOO!" phase.
          </p>

          {/* Upload Section */}
          <div className="bg-gray-700 border border-orange-500 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <label
                htmlFor="countdown-image-upload"
                className={`cursor-pointer bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                  uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Upload size={16} />
                {uploadingImage ? 'Uploading...' : 'Upload Image'}
              </label>
              <input
                id="countdown-image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                className="hidden"
              />
              
              {countdownImages.length > 0 && (
                <button
                  onClick={handleClearAllImages}
                  className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Clear All
                </button>
              )}
            </div>
            
            <p className="text-gray-400 text-xs">
              Supported formats: JPG, PNG, GIF, WebP. Max size: 5MB.
            </p>
          </div>

          {/* Images Grid */}
          {countdownImages.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-orange-300 font-medium">
                  Uploaded Images ({countdownImages.length})
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {countdownImages.map((image, index) => (
                  <div key={image.id || `image-${index}`} className="relative group">
                    <div className="aspect-square bg-gray-700 rounded-lg overflow-hidden border border-gray-600">
                      <img
                        src={image.data}
                        alt={image.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Image Info */}
                    <div className="mt-2">
                      <p className="text-gray-300 text-xs truncate" title={image.name}>
                        {image.name}
                      </p>
                      <p className="text-gray-500 text-xs">
                        {(image.size / 1024).toFixed(1)}KB
                      </p>
                    </div>

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDeleteImage(image.id)}
                      className="absolute top-2 right-2 bg-red-600 hover:bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete image"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Image size={48} className="mx-auto mb-3 opacity-50" />
              <p>No countdown images uploaded yet.</p>
              <p className="text-sm">Upload some images to surprise your viewers!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPreferences;
